/**
 * Monaco Editor Loader Configuration
 * 
 * Configures Monaco to load from jsDelivr CDN instead of bundling.
 * This avoids SES issues in Cloudflare Workers/Pages environment.
 * 
 * Type safety is preserved via the monaco-editor npm package.
 * 
 * IMPORTANT: This module no longer auto-initializes on import.
 * Call `ensureMonacoLoaded()` when a Monaco editor component is about to mount.
 */

import { loader } from '@monaco-editor/react';
import { _setMonacoLoaded } from './monaco-shim';

// Configure Monaco to load from jsDelivr CDN (idempotent)
loader.config({
  paths: {
    vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.55.1/min/vs'
  },
  'vs/nls': {
    availableLanguages: {}
  }
});

// Singleton promise to ensure we only initialize once
let monacoInitPromise: Promise<typeof import('monaco-editor')> | null = null;

/**
 * Ensures Monaco Editor is loaded from CDN. Safe to call multiple times —
 * subsequent calls return the same promise.
 * 
 * Call this in a useEffect when a Monaco editor component mounts,
 * NOT at the top level of App.tsx.
 */
export function ensureMonacoLoaded(): Promise<typeof import('monaco-editor')> {
  if (!monacoInitPromise) {
    monacoInitPromise = loader.init().then((monaco) => {
      // Notify the shim that Monaco is available
      _setMonacoLoaded(monaco);
      
      // Also set on window for global access
      (window as unknown as { monaco?: typeof monaco }).monaco = monaco;
      
      console.log('Monaco Editor loaded from CDN successfully');
      return monaco;
    }).catch((err) => {
      // Reset promise so it can be retried
      monacoInitPromise = null;
      console.error('Failed to load Monaco Editor from CDN:', err);
      throw err;
    });
  }
  return monacoInitPromise;
}